import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ScienceIcon from "@mui/icons-material/Science";
import { NotificationManager } from "react-notifications";
import AssignTestsDialog from "../common/AssignTestsDialog";
import AssignSlatesDialog from "../common/AssignSlatesDialog";
import CommonLoader from "../common/CommonLoader";
import { GetLabOrder, AddTestsToSample } from "../../services/OrderService";

// Pulls the API's own message off a ky HTTPError. Returns "" for a network
// failure or a body that is not the usual response payload.
const readApiMessage = async (error) => {
  try {
    const body = await error?.response?.json();
    return body?.message || "";
  } catch {
    return "";
  }
};

const sampleLabel = (sample) =>
  [
    `Sample ${sample.sampleNumber}`,
    sample.labId?.trim(),
    sample.sampleMatrix?.trim(),
  ]
    .filter(Boolean)
    .join(" - ");

/**
 * ENB-583: "+ New" on Analytical Results. Works like VLP order entry: pick the
 * sample, then add individual tests (Assign Tests) or a slate's tests
 * (Assign Slate). The shared pickers only return the chosen codes; this dialog
 * saves them as pending results on the sample.
 *
 * onTestsAdded receives the API result:
 * { testsAdded, samplesUpdated, dueDateChanged, reportDueDate, turnAroundTime }
 */
const AddAnalysisDialog = ({ open, onClose, orderNumber, accountNumber, onTestsAdded }) => {
  const [samples, setSamples] = useState([]);
  const [selectedSampleId, setSelectedSampleId] = useState("");
  const [assignToAllSamples, setAssignToAllSamples] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testsDialogOpen, setTestsDialogOpen] = useState(false);
  const [slatesDialogOpen, setSlatesDialogOpen] = useState(false);

  // Load the order's samples fresh each time, since samples can be added or
  // duplicated on this page after it first loaded.
  useEffect(() => {
    if (!open || !orderNumber) return;

    const loadSamples = async () => {
      try {
        setLoading(true);
        const response = await GetLabOrder(orderNumber);
        const orderSamples = [...(response.results?.samples || [])].sort(
          (a, b) => (a.sampleNumber ?? 0) - (b.sampleNumber ?? 0)
        );
        setSamples(orderSamples);
        setSelectedSampleId((prev) =>
          orderSamples.some((s) => s.recordid === prev) ? prev : orderSamples[0]?.recordid ?? ""
        );
      } catch (err) {
        NotificationManager.error("Error getting order samples: " + err.message, "Error");
      } finally {
        setLoading(false);
      }
    };

    setAssignToAllSamples(false);
    loadSamples();
  }, [open, orderNumber]);

  const selectedSample = samples.find((s) => s.recordid === selectedSampleId);

  // Called by the pickers. Throwing hands the message back to the picker so it
  // shows the failure instead of a success.
  const saveTests = async (testCodes, jobId = "") => {
    try {
      const response = await AddTestsToSample(orderNumber, selectedSample.recordid, {
        testCodes,
        jobId,
        assignToAllSamples,
      });
      const result = response.results;

      NotificationManager.success(response.message, "Success");
      if (result?.dueDateChanged) {
        NotificationManager.info(
          `Slate needs ${result.turnAroundTime} days turnaround. Report due date moved to ${new Date(
            result.reportDueDate
          ).toLocaleDateString()}.`,
          "Due Date Updated",
          8000
        );
      }
      onTestsAdded?.(result);
    } catch (error) {
      const message = (await readApiMessage(error)) || "Failed to add tests to sample";
      throw new Error(message);
    }
  };

  const handleTestsPicked = ({ testCodes }) => saveTests(testCodes);

  const handleSlatePicked = ({ jobId, testCodes }) => saveTests(testCodes, jobId);

  // The old "+ New" behavior, kept for entering a result by hand.
  const handleManualEntry = () => {
    window.open(`/vlp/edit/lab-orders/${orderNumber}/results/add`, "_blank");
  };

  const canAssign = !!selectedSample && !loading;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Add Analysis</DialogTitle>
        <DialogContent>
          {loading ? (
            <CommonLoader loading={loading} />
          ) : samples.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This order has no samples. Add a sample first.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose the sample, then add individual tests or a slate.
              </Typography>

              <TextField
                select
                label="Sample"
                size="small"
                fullWidth
                value={selectedSampleId}
                onChange={(e) => setSelectedSampleId(e.target.value)}
              >
                {samples.map((sample) => (
                  <MenuItem key={sample.recordid} value={sample.recordid}>
                    {sampleLabel(sample)}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={assignToAllSamples}
                    onChange={(e) => setAssignToAllSamples(e.target.checked)}
                    disabled={samples.length < 2}
                  />
                }
                label={`Assign Tests to All Samples (${samples.length})`}
              />

              <Box display="flex" gap={2} sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AssignmentIcon />}
                  onClick={() => setTestsDialogOpen(true)}
                  disabled={!canAssign}
                >
                  Assign Tests
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<ScienceIcon />}
                  onClick={() => setSlatesDialogOpen(true)}
                  disabled={!canAssign}
                >
                  Assign Slate
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button onClick={handleManualEntry}>Enter Result Manually</Button>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {selectedSample && (
        <>
          <AssignTestsDialog
            open={testsDialogOpen}
            onClose={() => setTestsDialogOpen(false)}
            itemType="Sample"
            itemLabel="Sample:"
            itemValue={String(selectedSample.sampleNumber)}
            matrix={selectedSample.sampleMatrix?.trim() || "All"}
            onAssignComplete={handleTestsPicked}
          />

          <AssignSlatesDialog
            open={slatesDialogOpen}
            onClose={() => setSlatesDialogOpen(false)}
            mode="SAMPLE"
            sampleNumber={String(selectedSample.sampleNumber)}
            orderNumber={orderNumber}
            accountNumber={accountNumber}
            onAssignComplete={handleSlatePicked}
          />
        </>
      )}
    </>
  );
};

export default AddAnalysisDialog;
